/**
 * Custom command filter utilities for cmdk components.
 * Provides better search result ranking than the default filter.
 */

/**
 * Creates a custom filter function that ranks results by match quality.
 * - Items starting with the search term rank highest (1.0)
 * - Keyword prefix matches rank next (0.95)
 * - Word boundary matches rank next (0.8)
 * - Substring matches rank lower (0.6)
 * - Keyword substring matches rank lowest (0.4)
 * - No match returns 0 (hidden)
 */
export function createSearchFilter() {
  return (value: string, search: string, keywords?: string[]): number => {
    const searchLower = search.toLowerCase().trim();
    if (!searchLower) return 1; // Show all when no search
    
    const valueLower = value.toLowerCase();
    
    // Perfect prefix match on value - highest score
    if (valueLower.startsWith(searchLower)) return 1;
    
    // Check keywords for prefix match
    if (keywords?.length) {
      for (const keyword of keywords) {
        const keywordLower = keyword.toLowerCase();
        if (keywordLower.startsWith(searchLower)) return 0.95;
        // Also check individual words within compound keywords
        const words = keywordLower.split(/[\s(),-]+/);
        for (const word of words) {
          if (word.startsWith(searchLower)) return 0.9;
        }
      }
    }
    
    // Word boundary match in value (e.g., "United States" matches "States")
    const valueWords = valueLower.split(/[\s(),-]+/);
    for (const word of valueWords) {
      if (word.startsWith(searchLower)) return 0.85;
    }
    
    // Substring match in value
    if (valueLower.includes(searchLower)) return 0.6;
    
    // Match in keywords anywhere
    if (keywords?.length) {
      const keywordsStr = keywords.join(' ').toLowerCase();
      if (keywordsStr.includes(searchLower)) return 0.4;
    }
    
    // No match
    return 0;
  };
}
